import React, { useState, useEffect } from 'react';
import { X, Plus, Edit2, Trash2, MapPin } from 'lucide-react';
import { useLocation } from '../../contexts/LocationContext';

const LocationModal = ({ mode, onClose, editLocation = null }) => {
  const { locations, addLocation, updateLocation, deleteLocation } = useLocation();
  const [activeTab, setActiveTab] = useState(mode === 'add' ? 'add' : 'manage');
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'United States'
  });
  const [editingId, setEditingId] = useState(null);
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(null);

  useEffect(() => {
    if (editLocation) {
      setFormData(editLocation);
      setEditingId(editLocation.id);
      setActiveTab('add');
    }
  }, [editLocation]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Location name is required';
    }
    
    if (!formData.address.trim()) {
      newErrors.address = 'Address is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setIsSaving(true);
      
      if (editingId) {
        await updateLocation(editingId, formData);
      } else {
        await addLocation(formData);
      }
      
      // Reset form
      setFormData({
        name: '',
        address: '',
        city: '',
        state: '',
        zipCode: '',
        country: 'United States'
      });
      setEditingId(null);
      setActiveTab('manage');
    } catch (error) {
      alert('Failed to save location. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleEdit = (location) => {
    setFormData(location);
    setEditingId(location.id);
    setActiveTab('add');
  };

  const handleDelete = async (locationId) => {
    if (window.confirm('Are you sure you want to delete this location?')) {
      try {
        setIsDeleting(locationId);
        const success = await deleteLocation(locationId);
        if (!success) {
          // Error message is already shown in deleteLocation function
          return;
        }
      } catch (error) {
        // Error handled silently
      } finally {
        setIsDeleting(null);
      }
    }
  };

  const handleCancel = () => {
    setFormData({
      name: '',
      address: '',
      city: '',
      state: '',
      zipCode: '',
      country: 'United States'
    });
    setEditingId(null);
    setErrors({});
    setActiveTab('manage');
  };

  return (
    <div className="modal-overlay">
      <div className="modal-container">
        <div className="modal-header">
          <h2>Location Management</h2>
          <button className="close-btn" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="modal-tabs">
          <button 
            className={`tab-btn ${activeTab === 'add' ? 'active' : ''}`}
            onClick={() => setActiveTab('add')}
          >
            <Plus size={16} />
            {editingId ? 'Edit Location' : 'Add Location'}
          </button>
          <button 
            className={`tab-btn ${activeTab === 'manage' ? 'active' : ''}`}
            onClick={() => setActiveTab('manage')}
          >
            <MapPin size={16} />
            Manage Locations
          </button>
        </div>

        <div className="modal-content">
          {activeTab === 'add' && (
            <form onSubmit={handleSubmit} className="location-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Location Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder="e.g., Main Store, Warehouse"
                    className={errors.name ? 'error' : ''}
                  />
                  {errors.name && <span className="error-text">{errors.name}</span>}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Address *</label>
                  <input
                    type="text"
                    value={formData.address}
                    onChange={(e) => handleInputChange('address', e.target.value)}
                    placeholder="Street address"
                    className={errors.address ? 'error' : ''}
                  />
                  {errors.address && <span className="error-text">{errors.address}</span>}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>City</label>
                  <input
                    type="text"
                    value={formData.city}
                    onChange={(e) => handleInputChange('city', e.target.value)}
                    placeholder="City"
                  />
                </div>
                <div className="form-group">
                  <label>State/Province</label>
                  <input
                    type="text"
                    value={formData.state}
                    onChange={(e) => handleInputChange('state', e.target.value)}
                    placeholder="State or Province"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>ZIP/Postal Code</label>
                  <input
                    type="text"
                    value={formData.zipCode}
                    onChange={(e) => handleInputChange('zipCode', e.target.value)}
                    placeholder="ZIP or Postal Code"
                  />
                </div>
                <div className="form-group">
                  <label>Country</label>
                  <select
                    value={formData.country}
                    onChange={(e) => handleInputChange('country', e.target.value)}
                  >
                    <option value="United States">United States</option>
                    <option value="Canada">Canada</option>
                    <option value="United Kingdom">United Kingdom</option>
                    <option value="Australia">Australia</option>
                    <option value="Germany">Germany</option>
                    <option value="France">France</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>



              <div className="form-actions">
                {editingId && (
                  <button type="button" className="cancel-btn" onClick={handleCancel} disabled={isSaving}>
                    Cancel
                  </button>
                )}
                <button type="submit" className="save-btn" disabled={isSaving}>
                  {isSaving ? 'Saving...' : (editingId ? 'Update Location' : 'Add Location')}
                </button>
              </div>
            </form>
          )}

          {activeTab === 'manage' && (
            <div className="locations-list">
              {locations.length === 0 ? (
                <div className="empty-state">
                  <MapPin size={48} />
                  <h3>No locations yet</h3>
                  <p>Add your first location to get started</p>
                  <button 
                    className="add-first-btn"
                    onClick={() => setActiveTab('add')}
                  >
                    Add Location
                  </button>
                </div>
              ) : (
                locations.map((location) => (
                  <div key={location.id} className="location-card">
                    <div className="location-details">
                      <h4>{location.name}</h4>
                      <p className="location-address">
                        {location.address}
                        {location.city && `, ${location.city}`}
                        {location.state && `, ${location.state}`}
                        {location.zipCode && ` ${location.zipCode}`}
                      </p>
                    </div>
                    <div className="location-actions">
                      <button 
                        className="action-btn edit-btn"
                        onClick={() => handleEdit(location)}
                        title="Edit location"
                      >
                        <Edit2 size={16} />
                      </button>
                      {locations.length > 1 && (
                        <button 
                          className="action-btn delete-btn"
                          onClick={() => handleDelete(location.id)}
                          title="Delete location"
                          disabled={isDeleting === location.id}
                        >
                          {isDeleting === location.id ? '...' : <Trash2 size={16} />}
                        </button>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>

      <style jsx>{`
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 20px;
        }

        .modal-container {
          background: white;
          border-radius: 12px;
          width: 100%;
          max-width: 600px;
          max-height: 90vh;
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }

        .modal-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 20px 24px;
          border-bottom: 1px solid #e1e5e9;
        }

        .modal-header h2 {
          margin: 0;
          font-size: 18px;
          font-weight: 600;
          color: #202223;
        }

        .close-btn {
          background: none;
          border: none;
          cursor: pointer;
          padding: 4px;
          border-radius: 4px;
          color: #6d7175;
        }

        .close-btn:hover {
          background: #f6f6f7;
        }

        .modal-tabs {
          display: flex;
          border-bottom: 1px solid #e1e5e9;
        }

        .tab-btn {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 12px 16px;
          border: none;
          background: none;
          cursor: pointer;
          font-size: 14px;
          color: #6d7175;
          transition: all 0.2s ease;
        }

        .tab-btn.active {
          color: #008060;
          border-bottom: 2px solid #008060;
          background: #f6f6f7;
        }

        .tab-btn:hover:not(.active) {
          background: #f6f6f7;
        }

        .modal-content {
          flex: 1;
          overflow-y: auto;
          padding: 24px;
        }

        .location-form {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .form-row {
          display: flex;
          gap: 16px;
        }

        .form-group {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .form-group label {
          font-size: 14px;
          font-weight: 500;
          color: #202223;
        }

        .form-group input,
        .form-group select {
          padding: 8px 12px;
          border: 1px solid #d2d5d8;
          border-radius: 6px;
          font-size: 14px;
          transition: border-color 0.2s ease;
        }

        .form-group input:focus,
        .form-group select:focus {
          outline: none;
          border-color: #008060;
          box-shadow: 0 0 0 2px rgba(0, 128, 96, 0.1);
        }

        .form-group input.error {
          border-color: #d72c0d;
        }

        .error-text {
          font-size: 12px;
          color: #d72c0d;
        }

        .form-actions {
          display: flex;
          gap: 12px;
          justify-content: flex-end;
          margin-top: 8px;
        }

        .cancel-btn {
          padding: 8px 16px;
          border: 1px solid #d2d5d8;
          background: white;
          color: #202223;
          border-radius: 6px;
          cursor: pointer;
          font-size: 14px;
          transition: all 0.2s ease;
        }

        .cancel-btn:hover {
          background: #f6f6f7;
        }

        .save-btn {
          padding: 8px 16px;
          border: 1px solid #008060;
          background: #008060;
          color: white;
          border-radius: 6px;
          cursor: pointer;
          font-size: 14px;
          transition: all 0.2s ease;
        }

        .save-btn:hover {
          background: #006b4a;
        }

        .locations-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .empty-state {
          text-align: center;
          padding: 40px 20px;
          color: #6d7175;
        }

        .empty-state h3 {
          margin: 16px 0 8px 0;
          color: #202223;
        }

        .add-first-btn {
          margin-top: 16px;
          padding: 8px 16px;
          border: 1px solid #008060;
          background: #008060;
          color: white;
          border-radius: 6px;
          cursor: pointer;
          font-size: 14px;
        }

        .location-card {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          padding: 16px;
          border: 1px solid #e1e5e9;
          border-radius: 8px;
          background: #fafbfb;
        }

        .location-details h4 {
          margin: 0 0 8px 0;
          font-size: 16px;
          color: #202223;
        }

        .location-address {
          margin: 0 0 8px 0;
          color: #6d7175;
          font-size: 14px;
        }

        .location-contact {
          margin: 4px 0;
          font-size: 12px;
          color: #6d7175;
        }

        .location-actions {
          display: flex;
          gap: 8px;
        }

        .action-btn {
          padding: 6px;
          border: 1px solid #d2d5d8;
          background: white;
          border-radius: 4px;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .edit-btn:hover {
          border-color: #008060;
          color: #008060;
        }

        .delete-btn:hover {
          border-color: #d72c0d;
          color: #d72c0d;
        }
      `}</style>
    </div>
  );
};

export default LocationModal;